/**
 * Phase 4 billing gate — the in-app half. Mounted ONCE in App.js (like
 * AccountClosingBanner), it rides every authenticated page and self-gates:
 *
 *   • BLOCKED (trial+grace expired, or subscription cancelled) → a full-screen
 *     paywall. Admins get an "add payment & reactivate" button; everyone else
 *     is held out with an "ask your admin" message. Suppressed on /settings so
 *     an admin can actually reach the billing tab to pay.
 *   • PAST_DUE (a paying customer's card failed) → a "update your card" bar.
 *   • TRIAL / GRACE → a countdown / urgency bar.
 *
 * Banners are ADMIN-ONLY (Rob's call — staff can't add payment, so we don't nag
 * them). The paywall applies to everyone, because a blocked company means
 * nobody should be working in it. Superadmins are never gated.
 *
 * One cheap GET drives all of it; for most users most of the time it returns an
 * "active" state and this renders nothing.
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { bottomDock } from '../lib/bottomDock';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, Clock, CreditCard, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { billingApi } from '../lib/api';
import { Button } from './ui/button';

// Fired by the billing page after a successful checkout so this re-checks
// without a full reload.
export const BILLING_STATUS_CHANGED = 'hh:billing-status-changed';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB'); } catch { return ''; }
}

function BottomBar({ tone, icon, children, cta, onCta }) {
  const skin = tone === 'red'
    ? 'border-red-500/30 bg-hive-danger-bg dark:bg-red-950'
    : 'border-hive-orange/30 bg-hive-pending-bg dark:bg-[#2A2A3A]';
  // ⚠️ Into the shared dock (lib/bottomDock.js), so it stacks with the account-closing bar and
  // the Slack offer. Since 29/10/2026 that's a slim strip across the TOP that pushes the page
  // down rather than a card floating over it (Rob: it hid things around the site).
  return createPortal(
    <div className={`border-b ${skin} px-4 py-1.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1`}>
      {icon}
      <div className="text-[13px] text-center">{children}</div>
      <Button size="sm" className="btn-primary flex-shrink-0 h-7 px-3 text-xs" onClick={onCta}>{cta}</Button>
    </div>,
    bottomDock(),
  );
}

export default function BillingStatus() {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState(null);

  const role = user?.role;

  useEffect(() => {
    let alive = true;
    const refresh = () => {
      if (!isAuthenticated) { setState(null); return; }
      billingApi.subscriptionState()
        .then((r) => { if (alive) setState(r.data); })
        .catch(() => { if (alive) setState(null); });
    };
    refresh();
    window.addEventListener(BILLING_STATUS_CHANGED, refresh);
    return () => { alive = false; window.removeEventListener(BILLING_STATUS_CHANGED, refresh); };
  }, [isAuthenticated, location.pathname]);

  if (!isAuthenticated || !state) return null;
  if (role === 'superadmin') return null;   // platform admin is never gated

  const onBillingArea = location.pathname.startsWith('/settings');

  // 1) HARD BLOCK — full-screen paywall. Suppressed on /settings so an admin can
  //    reach the billing tab and pay. Staff can't reach /settings anyway.
  if (state.blocked && !onBillingArea) {
    const isAdmin = role === 'admin';
    return (
      <div className="fixed inset-0 z-[60] bg-background/95 backdrop-blur flex items-center justify-center p-4">
        <div className="max-w-md w-full rounded-2xl border border-border bg-card shadow-xl p-8 text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-amber-500/15 flex items-center justify-center">
            <Lock className="w-6 h-6 text-amber-500" />
          </div>
          {/* SAY WHICH IT IS. `blocked` covers two quite different situations and this
              screen used to blame the trial for both — so a customer who had cancelled,
              or whose subscription was cancelled after a failed card, was told their
              trial had ended. Confusing at best, and it makes the app look like it has
              lost track of them. `on_trial` is already in the gate; it just wasn't
              being read. (Rob, 31/07/2026.) */}
          <h2 className="text-xl font-semibold mb-2">
            {state.on_trial
              ? 'Your HolidayHive trial has ended'
              : 'Your HolidayHive subscription isn’t active'}
          </h2>
          {isAdmin ? (
            <>
              <p className="text-muted-foreground mb-6">
                {state.on_trial
                  ? 'Add a payment method to reactivate your company’s account — everything is exactly as you left it.'
                  : 'Start it again to restore access — everything is exactly as you left it, and nothing has been deleted.'}
              </p>
              <Button className="btn-primary w-full" onClick={() => navigate('/settings?tab=billing')}>
                <CreditCard className="w-4 h-4 mr-2" /> Add payment &amp; reactivate
              </Button>
            </>
          ) : (
            <p className="text-muted-foreground mb-6">
              Please ask your company administrator to add a payment method to
              restore access.
            </p>
          )}
          <button className="mt-4 text-sm text-muted-foreground hover:underline" onClick={logout}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Banners below are ADMIN-ONLY.
  if (role !== 'admin') return null;

  // 2) DUNNING — a paying customer's card failed.
  if (state.past_due) {
    return (
      <BottomBar tone="red" cta="Update card" onCta={() => navigate('/settings?tab=billing')}
        icon={<CreditCard className="w-5 h-5 text-red-500 flex-shrink-0" />}>
        <span className="font-medium">A payment didn't go through.</span>{' '}
        <span className="hidden sm:inline text-muted-foreground">Update your card to avoid any interruption.</span>
      </BottomBar>
    );
  }

  // 3) TRIAL countdown.
  if (state.state === 'trial') {
    const d = state.days_left;
    return (
      <BottomBar tone="amber" cta="Add payment" onCta={() => navigate('/settings?tab=billing')}
        icon={<Clock className="w-5 h-5 text-amber-500 flex-shrink-0" />}>
        <span className="font-medium">
          {d === 1 ? 'Your free trial ends today' : `${d} days left on your free trial`}
        </span>{' '}
        <span className="hidden sm:inline text-muted-foreground">
          Add payment to keep everything running{state.trial_ends_at ? ` (ends ${fmtDate(state.trial_ends_at)})` : ''}.
        </span>
      </BottomBar>
    );
  }

  // 4) GRACE — trial ended, access about to pause.
  if (state.state === 'grace') {
    const g = state.grace_days_left ?? 0;
    return (
      <BottomBar tone="red" cta="Add payment" onCta={() => navigate('/settings?tab=billing')}
        icon={<AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />}>
        <span className="font-medium">Your trial has ended.</span>{' '}
        <span className="hidden sm:inline text-muted-foreground">
          Access pauses in {g} day{g === 1 ? '' : 's'} unless you add payment.
        </span>
      </BottomBar>
    );
  }

  return null;
}
